import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { useFpcLabels, getFpcLabel } from "@/lib/fpc";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  DatabaseZap,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { FoodsHeader } from "./components/FoodsHeader";
import { OFFSearchDialog } from "./components/OFFSearchDialog";
import { useStoredItemsPerPage } from "./hooks/useStoredItemsPerPage";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
  PAGE_RANGE_DISPLAYED,
  type SortField,
  type SortDirection,
} from "./types";
import { numericField, optionalField, convertNumericValue } from "./utils";
import {
  fetchProductByBarcode,
  parseOFFProduct,
  type ParsedOFFProduct,
} from "@/lib/openFoodFacts";

// ─── Form value type ──────────────────────────────────────────────────────────

interface FoodFormValues {
  nazov: string;
  kategoriaId: string;
  energiaKj: string;
  energiaKcal: string;
  proteinG: string;
  tukG: string;
  nmkG: string;
  transG: string;
  monoG: string;
  polyG: string;
  sacharidG: string;
  cukorG: string;
  vlakninaG: string;
  sodikG: string;
  solG: string;
  cholesterolMg: string;
  vapnikMg: string;
  vodaG: string;
  balenieG: string;
  poznamka: string;
  barcode: string;
}

const emptyForm: FoodFormValues = {
  nazov: "",
  kategoriaId: "",
  energiaKj: "",
  energiaKcal: "",
  proteinG: "",
  tukG: "",
  nmkG: "",
  transG: "",
  monoG: "",
  polyG: "",
  sacharidG: "",
  cukorG: "",
  vlakninaG: "",
  sodikG: "",
  solG: "",
  cholesterolMg: "",
  vapnikMg: "",
  vodaG: "",
  balenieG: "",
  poznamka: "",
  barcode: "",
};

function potravinaToForm(p: any): FoodFormValues {
  return {
    nazov: p.nazov ?? "",
    kategoriaId: p.kategoriaId?.toString() ?? "",
    energiaKj: p.energiaKj ?? "",
    energiaKcal: p.energiaKcal ?? "",
    proteinG: p.proteinG ?? "",
    tukG: p.tukG ?? "",
    nmkG: p.nmkG ?? "",
    transG: p.transG ?? "",
    monoG: p.monoG ?? "",
    polyG: p.polyG ?? "",
    sacharidG: p.sacharidG ?? "",
    cukorG: p.cukorG ?? "",
    vlakninaG: p.vlakninaG ?? "",
    sodikG: p.sodikG ?? "",
    solG: p.solG ?? "",
    cholesterolMg: p.cholesterolMg ?? "",
    vapnikMg: p.vapnikMg ?? "",
    vodaG: p.vodaG ?? "",
    balenieG: p.balenieG ?? "",
    poznamka: p.poznamka ?? "",
    barcode: p.barcode ?? "",
  };
}

function offToForm(off: ParsedOFFProduct): Partial<FoodFormValues> {
  return {
    nazov: off.nazov || "",
    energiaKj: off.energiaKj ?? "",
    energiaKcal: off.energiaKcal ?? "",
    proteinG: off.proteinG ?? "",
    tukG: off.tukG ?? "",
    nmkG: off.nmkG ?? "",
    transG: off.transG ?? "",
    monoG: off.monoG ?? "",
    polyG: off.polyG ?? "",
    sacharidG: off.sacharidG ?? "",
    cukorG: off.cukorG ?? "",
    vlakninaG: off.vlakninaG ?? "",
    sodikG: off.sodikG ?? "",
    solG: off.solG ?? "",
    cholesterolMg: off.cholesterolMg ?? "",
    vapnikMg: off.vapnikMg ?? "",
    vodaG: off.vodaG ?? "",
    balenieG: off.balenieG ?? "",
    poznamka: off.poznamka ?? "",
    barcode: off.barcode ?? "",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FoodsPage() {
  const { canManageFoods } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKategoria, setSelectedKategoria] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPotravina, setEditingPotravina] = useState<any>(null);
  const [fpcSearch, setFpcSearch] = useState("");
  const [fpcValue, setFpcValue] = useState<string>("");
  const [fpcDropdownOpen, setFpcDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useStoredItemsPerPage(
    "foods_items_per_page",
    DEFAULT_ITEMS_PER_PAGE
  );
  const [sortField, setSortField] = useState<SortField>("nazov");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Controlled form state
  const [formValues, setFormValues] = useState<FoodFormValues>(emptyForm);

  // OFF search dialog
  const [isOFFDialogOpen, setIsOFFDialogOpen] = useState(false);

  // "Fetch from OFF" loading state (for edit mode)
  const [fetchingOFF, setFetchingOFF] = useState(false);

  const { t } = useTranslation();

  const { data: potraviny, isLoading } = trpc.potravina.list.useQuery();
  const { data: kategorie } = trpc.kategoria.list.useQuery();

  const createMutation = trpc.potravina.create.useMutation();
  const updateMutation = trpc.potravina.update.useMutation();
  const deleteMutation = trpc.potravina.delete.useMutation();
  const utils = trpc.useUtils();
  const { fpcLabels } = useFpcLabels();

  // Build sorted FPC options from taxonomy
  const fpcOptions = useMemo(() => {
    return Object.entries(fpcLabels)
      .map(([kod, label]) => ({ kod, label }))
      .sort((a, b) => a.kod.localeCompare(b.kod));
  }, [fpcLabels]);

  const filteredFpcOptions = useMemo(() => {
    const q = fpcSearch.trim().toLowerCase();
    if (!q) return fpcOptions;
    return fpcOptions.filter(
      o => o.kod.toLowerCase().includes(q) || o.label.toLowerCase().includes(q)
    );
  }, [fpcOptions, fpcSearch]);

  // Filter and sort foods
  const filteredAndSortedPotraviny = useMemo(() => {
    if (!potraviny) return [];

    // First filter
    const filtered = potraviny.filter(p => {
      const matchesSearch = p.nazov
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesKategoria =
        !selectedKategoria || p.kategoriaId?.toString() === selectedKategoria;
      return matchesSearch && matchesKategoria;
    });

    // Then sort
    return [...filtered].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle null/undefined values
      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      // Handle numeric fields
      if (
        sortField === "energiaKj" ||
        sortField === "proteinG" ||
        sortField === "tukG" ||
        sortField === "sacharidG"
      ) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      // fpcKod: null/empty sorts last
      if (sortField === "fpcKod") {
        if (!aValue && bValue) return sortDirection === "asc" ? 1 : -1;
        if (aValue && !bValue) return sortDirection === "asc" ? -1 : 1;
      }

      // Compare
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [potraviny, searchTerm, selectedKategoria, sortField, sortDirection]);

  // Calculate pagination
  const totalItems = filteredAndSortedPotraviny.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Get current page items
  const currentItems = filteredAndSortedPotraviny.slice(startIndex, endIndex);

  // Generate page numbers for pagination controls
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const halfRange = Math.floor(PAGE_RANGE_DISPLAYED / 2);

    if (totalPages <= PAGE_RANGE_DISPLAYED) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show range with ellipsis
      let start = Math.max(1, currentPage - halfRange);
      let end = Math.min(totalPages, start + PAGE_RANGE_DISPLAYED - 1);

      // Adjust if we're at the beginning or end
      if (end - start + 1 < PAGE_RANGE_DISPLAYED) {
        start = Math.max(1, end - PAGE_RANGE_DISPLAYED + 1);
      }

      // Add first page and ellipsis if needed
      if (start > 1) {
        pages.push(1);
        if (start > 2) {
          pages.push("...");
        }
      }

      // Add page numbers
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push("...");
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // ── Form field helper ──────────────────────────────────────────────────────

  const setField = (field: keyof FoodFormValues, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  // ── Open add dialog ────────────────────────────────────────────────────────

  const openAddDialog = () => {
    setEditingPotravina(null);
    setFormValues(emptyForm);
    setFpcValue("");
    setFpcDropdownOpen(false);
    setIsDialogOpen(true);
  };

  // ── Open edit dialog ───────────────────────────────────────────────────────

  const openEditDialog = (potravina: any) => {
    setEditingPotravina(potravina);
    setFormValues(potravinaToForm(potravina));
    setFpcValue(potravina.fpcKod ?? "");
    setFpcDropdownOpen(false);
    setIsDialogOpen(true);
  };

  // ── Apply OFF product to form ──────────────────────────────────────────────

  const applyOFFProduct = (off: ParsedOFFProduct) => {
    setFormValues(prev => ({ ...prev, ...offToForm(off) }));
    // Note: category and FPC are intentionally NOT set – user must choose them
    toast.info(t("foods:off.importedNotice"));
  };

  // ── "Fetch from OFF" for existing food ────────────────────────────────────

  const handleFetchFromOFF = async () => {
    const barcode = formValues.barcode.trim();
    if (!barcode) {
      toast.error(t("foods:off.noBarcodeForFetch"));
      return;
    }
    setFetchingOFF(true);
    try {
      const product = await fetchProductByBarcode(barcode);
      if (!product) {
        toast.error(t("foods:off.barcodeNotFound"));
        return;
      }
      const parsed = parseOFFProduct(product);
      // Merge: only overwrite fields that are currently empty
      setFormValues(prev => {
        const merged: FoodFormValues = { ...prev };
        const off = offToForm(parsed);
        for (const key of Object.keys(off) as (keyof FoodFormValues)[]) {
          if (key === "nazov" || key === "kategoriaId") continue; // never overwrite name/category
          const offVal = off[key];
          if (offVal && !prev[key]) {
            (merged as any)[key] = offVal;
          }
        }
        return merged;
      });
      toast.success(t("foods:off.fetchSuccess"));
    } catch {
      toast.error(t("foods:off.fetchError"));
    } finally {
      setFetchingOFF(false);
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = {
      nazov: formValues.nazov,
      kategoriaId: formValues.kategoriaId
        ? Number(formValues.kategoriaId)
        : undefined,
      energiaKj: numericField(formValues.energiaKj),
      energiaKcal: numericField(formValues.energiaKcal),
      proteinG: numericField(formValues.proteinG),
      tukG: numericField(formValues.tukG),
      nmkG: numericField(formValues.nmkG),
      sacharidG: numericField(formValues.sacharidG),
      cukorG: numericField(formValues.cukorG),
      vlakninaG: numericField(formValues.vlakninaG),
      sodikG: numericField(formValues.sodikG),
      solG: numericField(formValues.solG),
      cholesterolMg: numericField(formValues.cholesterolMg),
      vapnikMg: numericField(formValues.vapnikMg),
      vodaG: numericField(formValues.vodaG),
      balenieG: optionalField(formValues.balenieG),
      poznamka: optionalField(formValues.poznamka),
      fpcKod: optionalField(fpcValue),
      transG: numericField(formValues.transG),
      monoG: numericField(formValues.monoG),
      polyG: numericField(formValues.polyG),
      barcode: optionalField(formValues.barcode),
    };

    try {
      if (editingPotravina) {
        await updateMutation.mutateAsync({ id: editingPotravina.id, data });
        toast.success(t("foods:messages.foodUpdated"));
      } else {
        await createMutation.mutateAsync(data);
        toast.success(t("foods:messages.foodCreated"));
      }
      utils.potravina.list.invalidate();
      setIsDialogOpen(false);
      setEditingPotravina(null);
      setFormValues(emptyForm);
      setFpcValue("");
      setFpcDropdownOpen(false);
      setCurrentPage(1); // Reset to first page after edit
    } catch (error) {
      toast.error(t("common:error"));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t("foods:messages.deleteConfirm"))) return;

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success(t("foods:messages.foodDeleted"));
      utils.potravina.list.invalidate();
      // If we deleted the last item on the page, go back a page
      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      toast.error(t("common:error"));
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset filters and pagination
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedKategoria("");
    setCurrentPage(1);
    setSortField("nazov");
    setSortDirection("asc");
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    const newItemsPerPage = parseInt(value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page

    // Calculate new total pages
    const newTotalPages = Math.ceil(totalItems / newItemsPerPage);
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages || 1);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-8">
        <FoodsHeader
          canManageFoods={canManageFoods}
          onAddFood={openAddDialog}
          onSearchOFF={
            canManageFoods ? () => setIsOFFDialogOpen(true) : undefined
          }
        />

        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("foods:searchPlaceholder")}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-4">
                <Select
                  value={selectedKategoria || "all"}
                  onValueChange={value => {
                    setSelectedKategoria(value === "all" ? "" : value);
                    setCurrentPage(1); // Reset to first page when changing category
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t("foods:allCategories")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("foods:allCategories")}
                    </SelectItem>
                    {kategorie?.map(k => (
                      <SelectItem key={k.id} value={k.id.toString()}>
                        {k.kategoria}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(searchTerm || selectedKategoria) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetFilters}
                  >
                    {t("foods:clearFilters")}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Stats and Controls */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                {t("foods:showing")}{" "}
                <span className="font-semibold">
                  {startIndex + 1}-{endIndex}
                </span>{" "}
                {t("foods:of")}{" "}
                <span className="font-semibold">{totalItems}</span>{" "}
                {t("foods:foods")}
                {searchTerm && (
                  <span className="ml-2">
                    {t("foods:for")} "
                    <span className="font-semibold">{searchTerm}</span>"
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4">
                {/* Sort Indicator */}
                <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t("foods:sortedBy")}:</span>
                  <span className="font-semibold capitalize">
                    {sortField === "nazov"
                      ? t("foods:sorting.name")
                      : sortField === "kategoriaNazov"
                        ? t("foods:sorting.category")
                        : sortField === "energiaKj"
                          ? t("foods:sorting.energy")
                          : sortField === "proteinG"
                            ? t("foods:sorting.protein")
                            : sortField === "tukG"
                              ? t("foods:sorting.fat")
                              : t("foods:sorting.carbs")}
                  </span>
                  {sortDirection === "asc" ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>

                {/* Rows per page selector */}
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="rowsPerPage"
                    className="text-sm whitespace-nowrap"
                  >
                    {t("foods:rowsPerPage")}:
                  </Label>
                  <Select
                    value={itemsPerPage.toString()}
                    onValueChange={handleItemsPerPageChange}
                  >
                    <SelectTrigger className="w-[80px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEMS_PER_PAGE_OPTIONS.map(option => (
                        <SelectItem key={option} value={option.toString()}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("common:loading")}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {/* Name column with sort */}
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("nazov")}
                        >
                          <div className="flex items-center gap-1">
                            {t("foods:table.name")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "nazov" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        {/* Category column with sort */}
                        <TableHead
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("kategoriaNazov")}
                        >
                          <div className="flex items-center gap-1">
                            {t("foods:table.category")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "kategoriaNazov" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        {/* Energy column with sort */}
                        <TableHead
                          className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("energiaKj")}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t("foods:table.energy")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "energiaKj" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        {/* Protein column with sort */}
                        <TableHead
                          className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("proteinG")}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t("foods:table.protein")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "proteinG" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        {/* Fat column with sort */}
                        <TableHead
                          className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("tukG")}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t("foods:table.fat")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "tukG" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        {/* Carbs column with sort */}
                        <TableHead
                          className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("sacharidG")}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {t("foods:table.carbs")}
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "sacharidG" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>

                        <TableHead
                          className="text-center cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort("fpcKod")}
                        >
                          <div className="flex items-center justify-center gap-1">
                            FPC
                            <ArrowUpDown className="h-4 w-4" />
                            {sortField === "fpcKod" &&
                              (sortDirection === "asc" ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              ))}
                          </div>
                        </TableHead>
                        <TableHead className="text-right">
                          {t("foods:table.actions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentItems.map(potravina => (
                        <TableRow key={potravina.id}>
                          <TableCell className="font-medium">
                            {potravina.nazov}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {potravina.kategoriaNazov || "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {potravina.energiaKj || "0"}
                          </TableCell>
                          <TableCell className="text-right">
                            {potravina.proteinG || "0"}
                          </TableCell>
                          <TableCell className="text-right">
                            {potravina.tukG || "0"}
                          </TableCell>
                          <TableCell className="text-right">
                            {potravina.sacharidG || "0"}
                          </TableCell>
                          <TableCell className="text-center">
                            {potravina.fpcKod ? (
                              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                                {potravina.fpcKod}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">
                                —
                              </span>
                            )}
                          </TableCell>
                          {canManageFoods && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(potravina)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(potravina.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      {currentItems.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-8 text-muted-foreground"
                          >
                            {searchTerm || selectedKategoria
                              ? t("foods:noResults")
                              : t("foods:noFoods")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t">
                    <div className="text-sm text-muted-foreground">
                      {t("foods:page")}{" "}
                      <span className="font-semibold">{currentPage}</span>{" "}
                      {t("foods:of")}{" "}
                      <span className="font-semibold">{totalPages}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* First Page */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="h-8 w-8"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>

                      {/* Previous Page */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-8 w-8"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {pageNumbers.map((page, index) =>
                          page === "..." ? (
                            <span
                              key={`ellipsis-${index}`}
                              className="px-2 text-muted-foreground"
                            >
                              ...
                            </span>
                          ) : (
                            <Button
                              key={`page-${page}`}
                              variant={
                                currentPage === page ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => handlePageChange(page as number)}
                              className="h-8 w-8 min-w-8 p-0"
                            >
                              {page}
                            </Button>
                          )
                        )}
                      </div>

                      {/* Next Page */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-8 w-8"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>

                      {/* Last Page */}
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="h-8 w-8"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {t("foods:goToPage")}:
                      <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={e => {
                          const page = parseInt(e.target.value);
                          if (!isNaN(page) && page >= 1 && page <= totalPages) {
                            handlePageChange(page);
                          }
                        }}
                        className="w-16"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ── OFF Search Dialog ── */}
        <OFFSearchDialog
          open={isOFFDialogOpen}
          onOpenChange={setIsOFFDialogOpen}
          onSelect={parsed => {
            // openAddDialog resets formValues to emptyForm;
            // we then immediately apply the OFF data on top of it.
            openAddDialog();
            // Use setTimeout(0) so the state update from openAddDialog
            // (which sets formValues to emptyForm) is batched first,
            // then we overlay the OFF data.
            setTimeout(() => applyOFFProduct(parsed), 0);
          }}
        />

        {/* ── Add/Edit Dialog ── */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPotravina
                  ? t("foods:dialog.editFood")
                  : t("foods:dialog.addNewFood")}
              </DialogTitle>
              <DialogDescription>
                {t("foods:dialog.nutritionalInfo")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 py-4">
                {/* Name */}
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="nazov">
                    {t("foods:dialog.nameRequired")}
                  </Label>
                  <Input
                    id="nazov"
                    name="nazov"
                    value={formValues.nazov}
                    onChange={e => setField("nazov", e.target.value)}
                    required
                  />
                </div>

                {/* Barcode + Fetch from OFF */}
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="barcode">{t("foods:dialog.barcode")}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="barcode"
                      name="barcode"
                      placeholder={t("foods:dialog.barcodePlaceholder")}
                      value={formValues.barcode}
                      onChange={e => setField("barcode", e.target.value)}
                      className="font-mono"
                    />
                    {editingPotravina && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleFetchFromOFF}
                        disabled={fetchingOFF || !formValues.barcode.trim()}
                        title={t("foods:off.fetchFromOFFTitle")}
                      >
                        {fetchingOFF ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <DatabaseZap className="h-4 w-4" />
                        )}
                        <span className="ml-1.5">
                          {t("foods:off.fetchFromOFF")}
                        </span>
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("foods:dialog.barcodeHint")}
                  </p>
                </div>

                {/* FPC Code */}
                <div className="space-y-2 col-span-2">
                  <Label>{t("foods:dialog.fpcCode")}</Label>
                  {/* Hidden input carries the value for the form */}
                  <input type="hidden" name="fpcKod" value={fpcValue} />
                  <div className="relative">
                    <button
                      type="button"
                      className="flex items-center justify-between w-full border rounded-md px-3 py-2 text-sm bg-background hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
                      onClick={() => {
                        setFpcDropdownOpen(v => !v);
                        setFpcSearch("");
                      }}
                    >
                      {fpcValue ? (
                        <span>
                          <span className="font-mono bg-muted px-1 rounded mr-2">
                            {fpcValue}
                          </span>
                          <span className="text-muted-foreground">
                            {getFpcLabel(fpcLabels, fpcValue)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          {t("foods:dialog.selectFpcCode")}
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 text-muted-foreground ml-2 shrink-0" />
                    </button>
                    {fpcDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg">
                        <div className="p-2 border-b">
                          <Input
                            autoFocus
                            placeholder={t("foods:dialog.searchFpcCode")}
                            value={fpcSearch}
                            onChange={e => setFpcSearch(e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="max-h-56 overflow-y-auto">
                          {/* Clear option */}
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50"
                            onClick={() => {
                              setFpcValue("");
                              setFpcDropdownOpen(false);
                            }}
                          >
                            {t("foods:dialog.noFpcCode")}
                          </button>
                          {filteredFpcOptions.map(opt => (
                            <button
                              key={opt.kod}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2 ${
                                fpcValue === opt.kod
                                  ? "bg-muted font-medium"
                                  : ""
                              }`}
                              onClick={() => {
                                setFpcValue(opt.kod);
                                setFpcDropdownOpen(false);
                              }}
                            >
                              <span className="font-mono bg-muted px-1 rounded shrink-0">
                                {opt.kod}
                              </span>
                              <span className="text-muted-foreground truncate">
                                {opt.label}
                              </span>
                            </button>
                          ))}
                          {filteredFpcOptions.length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              {t("foods:dialog.noFpcResults")}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("foods:dialog.fpcHint")}
                  </p>
                </div>

                {/* Category */}
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="kategoriaId">
                    {t("foods:dialog.category")}
                  </Label>
                  <Select
                    name="kategoriaId"
                    value={formValues.kategoriaId || ""}
                    onValueChange={v => setField("kategoriaId", v)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("foods:dialog.selectCategory")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {kategorie?.map(k => (
                        <SelectItem key={k.id} value={k.id.toString()}>
                          {k.kategoria}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!formValues.kategoriaId && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {t("foods:dialog.categoryRequired")}
                    </p>
                  )}
                </div>

                {/* Energy kJ */}
                <div className="space-y-2">
                  <Label htmlFor="energiaKj">
                    {t("foods:dialog.energyKj")}
                  </Label>
                  <Input
                    id="energiaKj"
                    name="energiaKj"
                    type="number"
                    step="0.01"
                    value={formValues.energiaKj}
                    onChange={e => setField("energiaKj", e.target.value)}
                  />
                </div>

                {/* Energy kcal */}
                <div className="space-y-2">
                  <Label htmlFor="energiaKcal">
                    {t("foods:dialog.energyKcal")}
                  </Label>
                  <Input
                    id="energiaKcal"
                    name="energiaKcal"
                    type="number"
                    step="0.01"
                    value={formValues.energiaKcal}
                    onChange={e => setField("energiaKcal", e.target.value)}
                  />
                </div>

                {/* Protein */}
                <div className="space-y-2">
                  <Label htmlFor="proteinG">{t("foods:dialog.proteinG")}</Label>
                  <Input
                    id="proteinG"
                    name="proteinG"
                    type="number"
                    step="0.01"
                    value={formValues.proteinG}
                    onChange={e => setField("proteinG", e.target.value)}
                  />
                </div>

                {/* Fat */}
                <div className="space-y-2">
                  <Label htmlFor="tukG">{t("foods:dialog.fatG")}</Label>
                  <Input
                    id="tukG"
                    name="tukG"
                    type="number"
                    step="0.01"
                    value={formValues.tukG}
                    onChange={e => setField("tukG", e.target.value)}
                  />
                </div>

                {/* Saturated fat */}
                <div className="space-y-2">
                  <Label htmlFor="nmkG">
                    {t("foods:dialog.saturatedFatG")}
                  </Label>
                  <Input
                    id="nmkG"
                    name="nmkG"
                    type="number"
                    step="0.01"
                    value={formValues.nmkG}
                    onChange={e => setField("nmkG", e.target.value)}
                  />
                </div>

                {/* Carbohydrates */}
                <div className="space-y-2">
                  <Label htmlFor="sacharidG">
                    {t("foods:dialog.carbohydratesG")}
                  </Label>
                  <Input
                    id="sacharidG"
                    name="sacharidG"
                    type="number"
                    step="0.01"
                    value={formValues.sacharidG}
                    onChange={e => setField("sacharidG", e.target.value)}
                  />
                </div>

                {/* Sugar */}
                <div className="space-y-2">
                  <Label htmlFor="cukorG">{t("foods:dialog.sugarG")}</Label>
                  <Input
                    id="cukorG"
                    name="cukorG"
                    type="number"
                    step="0.01"
                    value={formValues.cukorG}
                    onChange={e => setField("cukorG", e.target.value)}
                  />
                </div>

                {/* Fiber */}
                <div className="space-y-2">
                  <Label htmlFor="vlakninaG">{t("foods:dialog.fiberG")}</Label>
                  <Input
                    id="vlakninaG"
                    name="vlakninaG"
                    type="number"
                    step="0.01"
                    value={formValues.vlakninaG}
                    onChange={e => setField("vlakninaG", e.target.value)}
                  />
                </div>

                {/* Sodium */}
                <div className="space-y-2">
                  <Label htmlFor="sodikG">{t("foods:dialog.sodiumG")}</Label>
                  <Input
                    id="sodikG"
                    name="sodikG"
                    type="number"
                    step="0.01"
                    value={formValues.sodikG}
                    onChange={e => setField("sodikG", e.target.value)}
                  />
                </div>

                {/* Salt */}
                <div className="space-y-2">
                  <Label htmlFor="solG">{t("foods:dialog.saltG")}</Label>
                  <Input
                    id="solG"
                    name="solG"
                    type="number"
                    step="0.01"
                    value={formValues.solG}
                    onChange={e => setField("solG", e.target.value)}
                  />
                </div>

                {/* Trans fat */}
                <div className="space-y-2">
                  <Label htmlFor="transG">{t("foods:dialog.transFatG")}</Label>
                  <Input
                    id="transG"
                    name="transG"
                    type="number"
                    step="0.01"
                    value={formValues.transG}
                    onChange={e => setField("transG", e.target.value)}
                  />
                </div>

                {/* Mono fat */}
                <div className="space-y-2">
                  <Label htmlFor="monoG">
                    {t("foods:dialog.monounsaturatedFatG")}
                  </Label>
                  <Input
                    id="monoG"
                    name="monoG"
                    type="number"
                    step="0.01"
                    value={formValues.monoG}
                    onChange={e => setField("monoG", e.target.value)}
                  />
                </div>

                {/* Poly fat */}
                <div className="space-y-2">
                  <Label htmlFor="polyG">
                    {t("foods:dialog.polyunsaturatedFatG")}
                  </Label>
                  <Input
                    id="polyG"
                    name="polyG"
                    type="number"
                    step="0.01"
                    value={formValues.polyG}
                    onChange={e => setField("polyG", e.target.value)}
                  />
                </div>

                {/* Cholesterol */}
                <div className="space-y-2">
                  <Label htmlFor="cholesterolMg">
                    {t("foods:dialog.cholesterolMg")}
                  </Label>
                  <Input
                    id="cholesterolMg"
                    name="cholesterolMg"
                    type="number"
                    step="0.01"
                    value={formValues.cholesterolMg}
                    onChange={e => setField("cholesterolMg", e.target.value)}
                  />
                </div>

                {/* Calcium */}
                <div className="space-y-2">
                  <Label htmlFor="vapnikMg">
                    {t("foods:dialog.calciumMg")}
                  </Label>
                  <Input
                    id="vapnikMg"
                    name="vapnikMg"
                    type="number"
                    step="0.01"
                    value={formValues.vapnikMg}
                    onChange={e => setField("vapnikMg", e.target.value)}
                  />
                </div>

                {/* Water */}
                <div className="space-y-2">
                  <Label htmlFor="vodaG">{t("foods:dialog.waterG")}</Label>
                  <Input
                    id="vodaG"
                    name="vodaG"
                    type="number"
                    step="0.01"
                    value={formValues.vodaG}
                    onChange={e => setField("vodaG", e.target.value)}
                  />
                </div>

                {/* Package size */}
                <div className="space-y-2">
                  <Label htmlFor="balenieG">
                    {t("foods:dialog.packageSizeG")}
                  </Label>
                  <Input
                    id="balenieG"
                    name="balenieG"
                    type="number"
                    step="0.01"
                    value={formValues.balenieG}
                    onChange={e => setField("balenieG", e.target.value)}
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="poznamka">{t("foods:dialog.notes")}</Label>
                  <Input
                    id="poznamka"
                    name="poznamka"
                    value={formValues.poznamka}
                    onChange={e => setField("poznamka", e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter className="sticky bottom-0 bg-background border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  {t("common:cancel")}
                </Button>
                <Button type="submit">
                  {editingPotravina ? t("common:update") : t("common:create")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
